import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { Phone, Users, Briefcase, ChevronDown, ChevronUp } from 'lucide-react';
import Button from '../../components/ui/Button';
import Image from '../../components/AppImage';
import routeData from '../../data/routeData';

const RouteDetailsDynamic = () => {
  const { routeName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedFAQs, setExpandedFAQs] = useState({});
  const [selectedDay, setSelectedDay] = useState('');
  
  const route = routeData?.[routeName];
  const isRoundTrip = location?.state?.isRoundTrip || false;

  // Extract search query parameters
  const searchParams = new URLSearchParams(location?.search);
  const searchFrom = searchParams?.get('searchFrom');
  const searchTo = searchParams?.get('searchTo');

  // Use search query for display if available, otherwise use route data
  const displayFrom = searchFrom || route?.from;
  const displayTo = searchTo || route?.to;

  // Double the fare for round trip
  const adjustedVehicles = route?.vehicles?.map(vehicle => ({
    ...vehicle,
    price: isRoundTrip ? vehicle?.price * 2 : vehicle?.price
  }));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [routeName]);

  useEffect(() => {
    if (!route) {
      navigate('/');
    } else if (route?.dayOptions && route?.dayOptions?.length > 0) {
      setSelectedDay(route?.dayOptions?.[0]);
    }
  }, [route, navigate]);

  if (!route) {
    return null;
  }

  const handleSelectCar = (vehicle) => {
    navigate('/car-details', {
      state: {
        from: route?.from,
        to: route?.to,
        vehicle: vehicle?.name,
        price: vehicle?.price,
        seats: vehicle?.seats,
        luggage: vehicle?.luggage,
        image: vehicle?.image,
        alt: vehicle?.imageAlt
      }
    });
  };

  const handleViewCabs = (routeItem) => {
    const slug = `${routeItem?.from?.toLowerCase()}-${routeItem?.to?.toLowerCase()}`?.replace(/\s+/g, '-');
    navigate(`/route/${slug}`);
  };

  const toggleFAQ = (index) => {
    setExpandedFAQs((prev) => ({
      ...prev,
      [index]: !prev?.[index]
    }));
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TravelAction",
    "name": `${displayFrom} to ${displayTo} Taxi Service`,
    "description": `Book taxi from ${displayFrom} to ${displayTo}. Distance: ${route?.distance}. Starting at ₹${route?.vehicles?.[0]?.price}`,
    "provider": {
      "@type": "LocalBusiness",
      "name": route?.companyName,
      "telephone": route?.phone
    },
    "fromLocation": {
      "@type": "Place",
      "name": displayFrom
    },
    "toLocation": {
      "@type": "Place",
      "name": displayTo
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={`${displayFrom} to ${displayTo} Taxi | ${route?.companyName}`}
        description={`Book ${displayFrom} to ${displayTo} taxi with ${route?.companyName}. ${route?.distance} in ${route?.duration}. Starting ₹${route?.vehicles?.[0]?.price}. Call ${route?.phone}`}
        keywords={`${displayFrom?.toLowerCase()} to ${displayTo?.toLowerCase()} taxi, ${displayFrom?.toLowerCase()} to ${displayTo?.toLowerCase()} cab, taxi from ${displayFrom?.toLowerCase()} to ${displayTo?.toLowerCase()}`}
        structuredData={structuredData}
        ogTitle={`${displayFrom} to ${displayTo} Taxi | ${route?.companyName}`}
        ogDescription={`Book ${displayFrom} to ${displayTo} taxi with ${route?.companyName}. ${route?.distance} in ${route?.duration}. Starting ₹${route?.vehicles?.[0]?.price}. Call ${route?.phone}`}
        ogUrl={typeof window !== 'undefined' ? window.location?.href : ''}
        canonicalUrl={typeof window !== 'undefined' ? window.location?.href : ''}
      />
      <Header />
      {/* Single Heading Section */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 py-8 md:py-12 pt-24 md:pt-28">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-orange-500">
            {displayFrom} to {displayTo} {route?.dayOptions ? 'Tempo Traveller' : 'One Way Taxi Services'}
          </h1>
          {route?.companyName && (
            <p className="text-center text-gray-600 mt-2 text-lg">{route?.companyName}</p>
          )}
        </div>
      </div>

      {/* Day Selection Tabs - Only for Tempo Traveller Routes */}
      {route?.dayOptions && route?.dayOptions?.length > 0 && (
        <div className="bg-white shadow-sm py-6">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap gap-3 justify-center">
              {route?.dayOptions?.map((day) => (
                <button
                  key={day}
                  onClick={() => setSelectedDay(day)}
                  className={`px-6 md:px-8 py-3 rounded-full font-semibold text-sm md:text-base transition-all duration-200 ${
                    selectedDay === day
                      ? 'bg-yellow-400 text-gray-900 shadow-md'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-yellow-400'
                  }`}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Vehicle Selection Cards - Horizontal Layout */}
      <section className="py-8 md:py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto space-y-4">
            {adjustedVehicles?.map((vehicle) => (
              <div
                key={vehicle?.id}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-200"
              >
                <div className="flex flex-col md:flex-row items-center gap-4 p-4 md:p-6">
                  {/* Car Image */}
                  <div className="w-full md:w-48 lg:w-56 flex-shrink-0">
                    <div className="aspect-[4/3] overflow-hidden bg-gray-50 rounded-lg">
                      <Image
                        src={vehicle?.image}
                        alt={vehicle?.imageAlt}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>

                  {/* Car Details */}
                  <div className="flex-1 w-full">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="space-y-2">
                        <p className="text-xs md:text-sm text-gray-600 uppercase tracking-wide">{vehicle?.type}</p>
                        <h3 className="text-xl md:text-2xl font-bold text-gray-800">{vehicle?.name}</h3>
                      </div>
                    </div>
                  </div>

                  {/* Seats and Luggage */}
                  <div className="flex md:flex-col gap-4 md:gap-2 items-center md:items-start">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-600" />
                      <span className="text-sm md:text-base font-medium text-gray-700">{vehicle?.seats} Seats</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-blue-600" />
                      <span className="text-sm md:text-base font-medium text-gray-700">{vehicle?.luggage} Luggage</span>
                    </div>
                  </div>

                  {/* Price and Button */}
                  <div className="flex flex-col items-center md:items-end gap-3 w-full md:w-auto">
                    <div className="text-center md:text-right">
                      <p className="text-2xl md:text-3xl font-bold text-green-600">₹{vehicle?.price}/-</p>
                      <p className="text-xs text-gray-500">Inc. of Taxes*</p>
                    </div>
                    <Button
                      onClick={() => handleSelectCar(vehicle)}
                      className="w-full md:w-auto bg-yellow-400 hover:bg-yellow-500 text-black px-8 py-3 rounded-lg font-bold text-base transition-colors"
                    >
                      Select Car
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Visitor's Guide Section */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-orange-500 mb-8">
              Visitor's Guide
            </h2>
            
            <div className="space-y-6 text-gray-700 leading-relaxed">
              {route?.visitorsGuide?.title && (
                <h3 className="text-2xl md:text-3xl font-bold text-gray-800 text-center mb-6">
                  {route?.visitorsGuide?.title}
                </h3>
              )}
              
              {route?.visitorsGuide?.intro && (
                <p className="text-base md:text-lg">{route?.visitorsGuide?.intro}</p>
              )}
              
              {route?.visitorsGuide?.sections?.map((section, index) => (
                <div key={index} className="space-y-3">
                  {section?.title && (
                    <h3 className="text-xl md:text-2xl font-bold text-blue-600">{section?.title}</h3>
                  )}
                  {section?.content && (
                    <p className="text-base md:text-lg">{section?.content}</p>
                  )}
                  {section?.subsections && (
                    <div className="space-y-4 mt-4">
                      {section?.subsections?.map((subsection, subIndex) => (
                        <div key={subIndex} className="ml-4">
                          <h4 className="text-lg font-bold text-gray-800">
                            {subsection?.subtitle}
                          </h4>
                          <p className="text-base mt-2">
                            {subsection?.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Terms & Condition */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Terms & Condition</h3>
            <p className="text-gray-600">{route?.from} to {route?.to}</p>
          </div>
        </div>
      </section>
      {/* FAQs Section */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-orange-500 mb-8">
              FAQs
            </h2>
            
            <div className="space-y-4">
              {route?.faqs?.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
                  >
                    <span className="font-semibold text-gray-800 pr-4">{faq?.question}</span>
                    {expandedFAQs?.[index] ? (
                      <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    )}
                  </button>
                  {expandedFAQs?.[index] && (
                    <div className="px-6 py-4 bg-white border-t border-gray-200">
                      <p className="text-gray-700">{faq?.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Popular Routes From Origin */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-orange-500 mb-10">
            Popular Routes From {route?.from}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {route?.popularRoutesFrom?.map((routeItem, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6"
              >
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  {routeItem?.from} to {routeItem?.to}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Start from <span className="text-blue-600 font-bold">₹ {routeItem?.price} /-</span> only.
                </p>
                <Button
                  onClick={() => handleViewCabs(routeItem)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-semibold transition-colors"
                >
                  View Cabs
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Popular Routes To Destination */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-orange-500 mb-10">
            Popular Routes From {route?.to}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {route?.popularRoutesTo?.map((routeItem, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6"
              >
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  {routeItem?.from} to {routeItem?.to}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Start from <span className="text-blue-600 font-bold">₹ {routeItem?.price} /-</span> only.
                </p>
                <Button
                  onClick={() => handleViewCabs(routeItem)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-semibold transition-colors"
                >
                  View Cabs
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Contact Section */}
      <section className="py-12 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            We're here for you 24/7!
          </h2>
          <p className="text-lg mb-6">Give us a call anytime</p>
          <a
            href={`tel:${route?.phone}`}
            className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-xl hover:bg-gray-100 transition-colors"
          >
            <Phone className="w-6 h-6" />
            {route?.phone}
          </a>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default RouteDetailsDynamic;